import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const VIEW = 320; // square crop viewport (px)
const OUT_MAX = 1080; // final max output dimension
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

export default function SquareCropModal({ open, imageUrl, natural, sourceCanvas, onConfirm, onCancel }) {
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const vpRef = useRef(null);
  const drag = useRef({ pointers: new Map(), lastPos: null, lastDist: null });
  const live = useRef({ zoom: 1, off: { x: 0, y: 0 } });

  useEffect(() => {
    live.current = { zoom: 1, off: { x: 0, y: 0 } };
    setZoom(1);
    setOff({ x: 0, y: 0 });
    drag.current = { pointers: new Map(), lastPos: null, lastDist: null };
  }, [imageUrl]);

  // Escape closes only this modal, never the drawer beneath
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onCancel]);

  // Native pointer listeners: React's delegated pointer events do not reliably dispatch
  // inside portals, so drag/pinch/wheel are attached directly to the viewport element.
  useEffect(() => {
    const el = vpRef.current;
    if (!open || !el || !natural?.w) return;
    const d = drag.current;

    const coverFit = () => VIEW / Math.min(natural.w, natural.h);
    const clamp = (o, scale) => ({
      x: Math.min(0, Math.max(VIEW - natural.w * scale, o.x)),
      y: Math.min(0, Math.max(VIEW - natural.h * scale, o.y)),
    });
    const applyOff = (no) => {
      const c = clamp(no, coverFit() * live.current.zoom);
      live.current.off = c;
      setOff(c);
    };
    const applyZoom = (z) => {
      const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
      live.current.zoom = nz;
      setZoom(nz);
      applyOff(live.current.off);
    };

    const down = (e) => {
      el.setPointerCapture?.(e.pointerId);
      d.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      d.lastPos = { x: e.clientX, y: e.clientY };
      d.lastDist = null;
    };
    const move = (e) => {
      if (!d.pointers.has(e.pointerId)) return;
      d.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const pts = [...d.pointers.values()];
      if (pts.length === 2) {
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (d.lastDist) applyZoom(live.current.zoom * (dist / d.lastDist));
        d.lastDist = dist;
      } else if (pts.length === 1 && d.lastPos) {
        applyOff({ x: live.current.off.x + (e.clientX - d.lastPos.x), y: live.current.off.y + (e.clientY - d.lastPos.y) });
        d.lastPos = { x: e.clientX, y: e.clientY };
      }
    };
    const up = (e) => {
      d.pointers.delete(e.pointerId);
      d.lastDist = null;
    };
    const wheel = (e) => {
      e.preventDefault();
      applyZoom(live.current.zoom * (e.deltaY < 0 ? 1.1 : 0.9));
    };

    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    el.addEventListener("wheel", wheel, { passive: false });
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      el.removeEventListener("wheel", wheel);
    };
  }, [open, imageUrl, natural]);

  if (!open || !imageUrl || !natural?.w) return null;

  const coverFit = VIEW / Math.min(natural.w, natural.h);
  const ds = coverFit * zoom;
  const w = natural.w * ds;
  const h = natural.h * ds;

  const confirm = async () => {
    setBusy(true);
    try {
      const sx = -off.x / ds;
      const sy = -off.y / ds;
      const ss = VIEW / ds;
      const outSize = Math.min(OUT_MAX, Math.max(1, Math.round(ss)));
      const out = document.createElement("canvas");
      out.width = outSize;
      out.height = outSize;
      const ctx = out.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(sourceCanvas, sx, sy, ss, ss, 0, 0, outSize, outSize);
      // Prefer WebP; browsers without WebP canvas encoding silently return PNG, so verify the blob type
      let blob = await new Promise((r) => out.toBlob(r, "image/webp", 0.82));
      if (!blob || blob.type !== "image/webp") blob = await new Promise((r) => out.toBlob(r, "image/jpeg", 0.85));
      if (!blob) throw new Error("encode failed");
      const ext = blob.type === "image/webp" ? "webp" : "jpg";
      onConfirm(new File([blob], `product-photo.${ext}`, { type: blob.type }));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div data-testid="photo-crop-modal" className="pointer-events-auto fixed inset-0 z-[110] flex items-center justify-center bg-black/90 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-4">
        <p className="font-heading text-base font-bold">Adjust product photo</p>
        <p className="text-xs text-muted-foreground">Drag to reposition · pinch or slider to zoom · saved as a square</p>
        <div
          ref={vpRef}
          data-testid="photo-crop-viewport"
          className="relative mx-auto mt-3 touch-none overflow-hidden rounded-md border bg-stone-100"
          style={{ width: VIEW, height: VIEW, maxWidth: "100%" }}
        >
          <img
            src={imageUrl}
            alt="Crop preview"
            data-testid="photo-crop-image"
            draggable={false}
            className="absolute max-w-none select-none"
            style={{ width: w, height: h, left: off.x, top: off.y }}
          />
        </div>
        <input
          data-testid="photo-crop-zoom"
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.01}
          value={zoom}
          onChange={(e) => {
            const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, parseFloat(e.target.value)));
            live.current.zoom = nz;
            setZoom(nz);
            const c = {
              x: Math.min(0, Math.max(VIEW - natural.w * coverFit * nz, live.current.off.x)),
              y: Math.min(0, Math.max(VIEW - natural.h * coverFit * nz, live.current.off.y)),
            };
            live.current.off = c;
            setOff(c);
          }}
          className="mt-3 w-full"
          aria-label="Zoom"
        />
        <div className="mt-3 flex gap-2">
          <Button type="button" variant="outline" data-testid="photo-crop-cancel" className="h-12 flex-1" onClick={onCancel}>
            <X className="h-4 w-4" /> Cancel
          </Button>
          <Button type="button" data-testid="photo-crop-confirm" className="h-12 flex-1" onClick={confirm} disabled={busy}>
            <Check className="h-4 w-4" /> {busy ? "Saving…" : "Use this crop"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
